import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from '../../../util/reactIntl';
import { getUserLocation, calculateDistance } from '../../../util/analytics';
import { createInstance, createImageVariantConfig } from '../../../util/sdkLoader';
import { NamedLink } from '../../../components';
import { ensureListing } from '../../../util/data';
import { formatPriceWithFees } from '../../../util/listingHelpers';

import css from './SectionSimilarListings.module.css';

// Create a lightweight SDK instance for client-side queries
let sdkInstance = null;

function getClientSdk() {
  if (!sdkInstance) {
    const clientId = process.env.REACT_APP_SHARETRIBE_SDK_CLIENT_ID;
    if (clientId) {
      const baseUrl = process.env.REACT_APP_SHARETRIBE_SDK_BASE_URL;
      sdkInstance = createInstance({
        clientId,
        ...(baseUrl ? { baseUrl } : {}),
      });
    }
  }
  return sdkInstance;
}

const SimilarListingCard = ({ listing, userLocation, routeConfiguration }) => {
  const currentListing = ensureListing(listing);
  const publicData = currentListing.attributes?.publicData || {};
  const geolocation = currentListing.attributes?.geolocation;
  // Base price Money (cents). Rendered all-in (base + 15% fee) below —
  // never the bare base price, which would leak our markup. The old
  // code read `publicData.price` (a field nothing writes) and also
  // omitted `price` from the query, so every card showed "$0/day".
  const price = currentListing.attributes?.price;

  // Calculate distance if both locations are available
  let distance = null;
  if (userLocation?.lat && userLocation?.lng && geolocation?.lat && geolocation?.lng) {
    distance = calculateDistance(
      userLocation.lat,
      userLocation.lng,
      geolocation.lat,
      geolocation.lng
    );
  }

  // Get first image with fallback variant chain
  const images = currentListing.images || [];
  const firstImage = images.length > 0 ? images[0] : null;
  const imageUrl = firstImage?.attributes?.variants?.['listing-card']?.url
    || firstImage?.attributes?.variants?.['scaled-small']?.url
    || firstImage?.attributes?.variants?.['listing-card-2x']?.url
    || null;

  // Create slug from title
  const slug = currentListing.attributes?.title
    ? currentListing.attributes.title
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '-')
        .replace(/-+/g, '-')
    : 'listing';

  return (
    <NamedLink
      name="ListingPage"
      params={{ slug, id: currentListing.id.uuid }}
      className={css.similarCard}
    >
      <div className={css.cardImage}>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={currentListing.attributes.title}
            className={css.image}
            loading="lazy"
          />
        ) : (
          <div className={css.placeholderImage}>No photo</div>
        )}
        {distance != null && (
          <div className={css.distanceBadge}>📍 {distance.toFixed(1)} mi</div>
        )}
      </div>
      <div className={css.cardContent}>
        <h4 className={css.cardTitle}>{currentListing.attributes.title}</h4>
        <div className={css.cardPrice}>
          {price ? `${formatPriceWithFees(price.amount)}/day` : ''}
        </div>
      </div>
    </NamedLink>
  );
};

const SectionSimilarListings = ({
  listing,
  className = '',
  routeConfiguration,
  intl,
}) => {
  const [similarListings, setSimilarListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);

  const currentListing = ensureListing(listing);
  const publicData = currentListing?.attributes?.publicData || {};
  const category = publicData.categoryLevel1 || publicData.category;

  useEffect(() => {
    const savedLocation = getUserLocation();
    setUserLocation(savedLocation);
    fetchSimilarListings(savedLocation);
  }, []);

  const fetchSimilarListings = async savedLocation => {
    if (!currentListing) {
      setLoading(false);
      return;
    }

    const clientSdk = getClientSdk();
    if (!clientSdk) {
      setLoading(false);
      return;
    }

    try {
      const searchParams = {
        include: ['images'],
        'fields.listing': ['title', 'geolocation', 'publicData', 'price'],
        'fields.image': ['variants.listing-card', 'variants.scaled-small'],
        // listing-card is a CUSTOM variant — its definition must ride
        // along or the API returns variant-less images (the 📸-fallback
        // cause; same bug as favorites #349).
        ...createImageVariantConfig('listing-card', 400, 1),
        per_page: 5, // Request 5 to have room after excluding current listing
      };

      // Add category filter if available
      if (category) {
        searchParams['pub_categoryLevel1'] = category;
      }

      // Add location-based bounds if available
      if (savedLocation?.lat && savedLocation?.lng) {
        const radiusMiles = savedLocation.radius || 15;
        const radiusKm = radiusMiles * 1.60934;
        const latDelta = radiusKm / 111.32;
        const lngDelta =
          radiusKm / (111.32 * Math.cos((savedLocation.lat * Math.PI) / 180));

        searchParams.bounds = `${savedLocation.lat + latDelta},${
          savedLocation.lng + lngDelta
        },${savedLocation.lat - latDelta},${savedLocation.lng - lngDelta}`;
      }

      const response = await clientSdk.listings.query(searchParams);

      if (response.data) {
        // Filter out current listing and limit to 4
        const filtered = response.data.data
          .filter(l => l.id.uuid !== currentListing.id.uuid)
          .slice(0, 4);
        setSimilarListings(filtered);
      }
    } catch (error) {
      console.error('Failed to fetch similar listings:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || similarListings.length === 0) {
    return null;
  }

  const classes = classNames(css.root, className);

  return (
    <section className={classes}>
      <div className={css.header}>
        <h3 className={css.title}>
          <FormattedMessage
            id="SectionSimilarListings.title"
            defaultMessage="Similar Kits Near You"
          />
        </h3>
        {category && (
          <NamedLink name="SearchPage" to={{ search: `?pub_categoryLevel1=${category}` }} className={css.viewAllLink}>
            <FormattedMessage
              id="SectionSimilarListings.viewAll"
              values={{ category: category || '' }}
              defaultMessage="See all {category} kits →"
            />
          </NamedLink>
        )}
      </div>

      <div className={css.grid}>
        {similarListings.map(similarListing => (
          <SimilarListingCard
            key={similarListing.id.uuid}
            listing={similarListing}
            userLocation={userLocation}
            routeConfiguration={routeConfiguration}
          />
        ))}
      </div>
    </section>
  );
};

export default SectionSimilarListings;
